import React, { useState, useEffect } from 'react';
import { Calculator } from 'lucide-react';
import { SEO } from '../components/SEO';

interface AuthorshipState {
  amount: string;
  city: string;
  taxRate: string;
  pensionType: 'none' | 'first' | 'both';
  expenses: string;
  isGross: boolean;
}

interface AuthorshipResult {
  grossAmount: number;
  netAmount: number;
  tax: number;
  pension: number;
  expenses: number;
  cityTax: number;
}

const cities = {
  'Zagreb': 18.0,
  'Split': 15.0,
  'Rijeka': 14.0,
  'Osijek': 13.0,
  'Zadar': 12.0,
  'Pula': 12.0,
  'Varaždin': 12.0,
  'Other': 10.0
};

function KalkulatorAutorskogHonorara() {
  const [values, setValues] = useState<AuthorshipState>({
    amount: '',
    city: 'Zagreb',
    taxRate: '24',
    pensionType: 'none',
    expenses: '30',
    isGross: true
  });

  const [results, setResults] = useState<AuthorshipResult | null>(null);

  const calculateHonorar = () => {
    let amount = parseFloat(values.amount);
    if (isNaN(amount)) return null;

    const taxRate = parseFloat(values.taxRate) / 100;
    const expenses = parseFloat(values.expenses) / 100;
    const cityTaxRate = cities[values.city as keyof typeof cities] / 100;
    
    let grossAmount = amount;
    if (!values.isGross) {
      // Calculate gross from net
      grossAmount = amount / (1 - (taxRate * (1 + cityTaxRate)) - (values.pensionType === 'both' ? 0.10 : values.pensionType === 'first' ? 0.075 : 0));
    }

    const expenseAmount = grossAmount * expenses;
    const taxableAmount = grossAmount - expenseAmount;
    
    let pensionAmount = 0;
    if (values.pensionType === 'first') {
      pensionAmount = taxableAmount * 0.075;
    } else if (values.pensionType === 'both') {
      pensionAmount = taxableAmount * 0.10;
    }

    const taxBase = taxableAmount - pensionAmount;
    const tax = taxBase * taxRate;
    const cityTax = tax * cityTaxRate;
    
    const netAmount = grossAmount - tax - cityTax - pensionAmount;

    return {
      grossAmount,
      netAmount,
      tax,
      pension: pensionAmount,
      expenses: expenseAmount,
      cityTax
    };
  };

  const handleInputChange = (field: keyof AuthorshipState, value: string | boolean) => {
    setValues(prev => ({
      ...prev,
      [field]: value
    }));
  };

  useEffect(() => {
    if (values.amount) {
      const results = calculateHonorar();
      setResults(results);
    }
  }, [values]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('hr-HR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Kalkulator Autorskog Honorara",
    "applicationCategory": "CalculatorApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR"
    },
    "description": "Izračunajte autorski honorar, poreze i doprinose. Precizni izračuni za različite gradove i stope poreza.",
    "url": "https://online-kalkulator.com/kalkulator-autorskog-honorara",
    "inLanguage": "hr",
    "author": {
      "@type": "Organization",
      "name": "Online Kalkulator"
    }
  };

  return (
    <>
      <SEO 
        title="Kalkulator Autorskog Honorara | Kalkulator Drugog Dohotka 2025"
        description="Izračunajte autorski honorar, poreze i doprinose. Precizni izračuni kalkulator za izračun drugog dohotka."
        canonicalUrl="/kalkulator-autorskog-honorara"
        structuredData={structuredData}
      />
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center justify-center gap-3 mb-6">
          <Calculator className="w-8 h-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-800 text-center">Kalkulator Autorskog Honorara</h1>
        </div>

        <div className="max-w-2xl mx-auto">
          {/* Calculator Section */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <div className="space-y-6">
              {/* Input Fields */}
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Iznos
                  </label>
                  <input
                    type="number"
                    value={values.amount}
                    onChange={(e) => handleInputChange('amount', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Unesite iznos"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Grad/Općina
                  </label>
                  <select
                    value={values.city}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {Object.entries(cities).map(([city, rate]) => (
                      <option key={city} value={city}>{city} ({rate}%)</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Stopa poreza
                  </label>
                  <select
                    value={values.taxRate}
                    onChange={(e) => handleInputChange('taxRate', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="24">24%</option>
                    <option value="30">30%</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Mirovinsko osiguranje
                  </label>
                  <select
                    value={values.pensionType}
                    onChange={(e) => handleInputChange('pensionType', e.target.value as AuthorshipState['pensionType'])}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="none">Bez MIO</option>
                    <option value="first">I. stup (7.5%)</option>
                    <option value="both">I. i II. stup (10%)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Paušalni troškovi
                  </label>
                  <select
                    value={values.expenses}
                    onChange={(e) => handleInputChange('expenses', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="30">30%</option>
                    <option value="0">0%</option>
                  </select>
                </div>

                <div className="col-span-2">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={values.isGross}
                      onChange={(e) => handleInputChange('isGross', e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Uneseni iznos je bruto</span>
                  </label>
                </div>
              </div>

              {/* Results */}
              {results && (
                <div className="space-y-4 border-t pt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 p-4 rounded-lg text-center">
                      <div className="text-gray-600 text-sm mb-1">Bruto iznos</div>
                      <div className="text-xl font-bold text-blue-600">
                        {formatCurrency(results.grossAmount)}
                      </div>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg text-center">
                      <div className="text-gray-600 text-sm mb-1">Neto iznos</div>
                      <div className="text-xl font-bold text-blue-600">
                        {formatCurrency(results.netAmount)}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Paušalni troškovi:</span>
                      <span className="font-semibold">{formatCurrency(results.expenses)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Mirovinsko osiguranje:</span>
                      <span className="font-semibold">{formatCurrency(results.pension)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Porez na dohodak:</span>
                      <span className="font-semibold">{formatCurrency(results.tax)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Prirez:</span>
                      <span className="font-semibold">{formatCurrency(results.cityTax)}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Information Section */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="space-y-8">
              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">O Kalkulatoru Autorskog Honorara</h2>
                <p className="text-gray-600 leading-relaxed">
                  Kalkulator autorskog honorara pomaže vam izračunati neto iznos honorara nakon odbitka
                  poreza, prireza i doprinosa. Uzima u obzir različite stope poreza, prireza po
                  gradovima te mogućnost mirovinskog osiguranja.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Kako se Računa</h2>
                <ol className="list-decimal list-inside text-gray-600 space-y-2">
                  <li>Od bruto iznosa oduzimaju se paušalni troškovi (30% ili 0%)</li>
                  <li>Na preostali iznos obračunava se doprinos za mirovinsko (ako je odabrano)</li>
                  <li>Na preostali iznos obračunava se porez na dohodak (24% ili 30%)</li>
                  <li>Na iznos poreza obračunava se prirez prema mjestu prebivališta</li>
                  <li>Neto iznos je bruto umanjen za sve poreze i doprinose</li>
                </ol>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Stope Prireza</h2>
                <div className="grid grid-cols-2 gap-4">
                  {Object.entries(cities).map(([city, rate]) => (
                    <div key={city} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                      <span className="font-medium">{city}:</span>
                      <span>{rate}%</span>
                    </div>
                  ))}
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Važne Napomene</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Izračun je informativne prirode</li>
                  <li>Paušalni troškovi od 30% priznaju se za većinu autorskih djela</li>
                  <li>Stopa poreza ovisi o visini dohotka i poreznom razredu</li>
                  <li>Mirovinsko osiguranje je opcionalno za autorske honorare</li>
                  <li>Za točan izračun konzultirajte poreznog savjetnika</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Što je Autorski Honorar</h2>
                <p className="text-gray-600 mb-4">
                  Autorski honorar je naknada koja se isplaćuje za:
                </p>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Književna, znanstvena i umjetnička djela</li>
                  <li>Računalne programe i baze podataka</li>
                  <li>Glazbena i audiovizualna djela</li>
                  <li>Fotografska i druga umjetnička djela</li>
                  <li>Prijevode i lekture</li>
                  <li>Ostala autorska djela zaštićena autorskim pravom</li>
                </ul>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default KalkulatorAutorskogHonorara;